import { storagePrefixFixedV2 } from '@/constant';

export type FieldName = {
  name: BodyPartMeasureName;
  label: string;
  unit: string;
  imageFiledName: string;
  value: number;
  imageSrc?: string;
  defaultValue?: number;
  maxValue?: number;
  minValue?: number;
  bodyPartImg?: string;
  order?: number; // 用于单次服务记录围度变化拍序
};

export type MeasurementValue = {
  exampleImageSrc?: string;
  bodyPartName?: string;
  measuredItemValue: FieldName[];
};

const BACK_THICK_NESS: FieldName[] = [
  {
    name: 'backThickness',
    label: '背厚',
    unit: '厘米',
    imageFiledName: 'backImage',
    value: 0,
  },
];

const WAIST_CIRCUMFERENCE: FieldName[] = [
  {
    name: 'waistCircumference',
    label: '腰围',
    unit: '厘米',
    imageFiledName: 'waistImage',
    value: 0,
  },
];

const MANUAL_MEASUREMENT: FieldName[] = [
  {
    name: 'leftArmCircumference',
    label: '左臂',
    unit: '厘米',
    imageFiledName: 'leftArmImage',
    value: 0,
    order: 1,
  },
  {
    name: 'rightArmCircumference',
    label: '右臂',
    unit: '厘米',
    imageFiledName: 'rightArmImage',
    value: 0,
    order: 4,
  },
  {
    name: 'leftThighCircumference',
    label: '左大腿',
    unit: '厘米',
    imageFiledName: 'leftThighImage',
    value: 0,
    order: 2,
  },
  {
    name: 'rightThighCircumference',
    label: '右大腿',
    unit: '厘米',
    imageFiledName: 'rightThighImage',
    value: 0,
    order: 5,
  },
  {
    name: 'leftCalfCircumference',
    label: '左小腿',
    unit: '厘米',
    imageFiledName: 'leftCalfImage',
    value: 0,
    order: 3,
  },
  {
    name: 'rightCalfCircumference',
    label: '右小腿',
    unit: '厘米',
    imageFiledName: 'rightCalfImage',
    value: 0,
    order: 6,
  },
];

export const FiledNameByBodyPart: Record<Service.BodyPart, FieldName[]> = {
  Shoulder: BACK_THICK_NESS,
  Abdomen: WAIST_CIRCUMFERENCE,
  Waist: WAIST_CIRCUMFERENCE,
  Back: BACK_THICK_NESS,
  All: MANUAL_MEASUREMENT,
  LowerBody: MANUAL_MEASUREMENT,
};

const MEASURED_ITEM_VALUE_BY_BACK: MeasurementValue[] = [
  {
    exampleImageSrc: storagePrefixFixedV2 + '/measure-girth-specification/example-image/back-specification.png',
    bodyPartName: '背厚',
    measuredItemValue: [
      {
        name: 'backThickness',
        label: '背厚',
        unit: '厘米',
        imageFiledName: 'backImage',
        value: 0,
        defaultValue: 30,
        minValue: 10,
        maxValue: 60,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/back.png',
      },
    ],
  },
];

const MEASURED_ITEM_VALUE_BY_WAIST: MeasurementValue[] = [
  {
    exampleImageSrc: storagePrefixFixedV2 + '/measure-girth-specification/example-image/waist-specification.png',
    bodyPartName: '腰围',
    measuredItemValue: [
      {
        name: 'waistCircumference',
        label: '腰围',
        unit: '厘米',
        imageFiledName: 'waistImage',
        defaultValue: 80,
        minValue: 40,
        maxValue: 150,
        value: 0,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/waist.png',
      },
    ],
  },
];

const MEASURED_ITEM_VALUE_ALL: MeasurementValue[] = [
  {
    exampleImageSrc: storagePrefixFixedV2 + '/measure-girth-specification/example-image/arm-specification.png',
    bodyPartName: '左/右臂围',
    measuredItemValue: [
      {
        name: 'leftArmCircumference',
        label: '左臂',
        unit: '厘米',
        imageFiledName: 'leftArmImage',
        value: 0,
        defaultValue: 25,
        minValue: 10,
        maxValue: 60,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/left-arm.png',
      },
      {
        name: 'rightArmCircumference',
        label: '右臂',
        unit: '厘米',
        imageFiledName: 'rightArmImage',
        value: 0,
        defaultValue: 25,
        minValue: 10,
        maxValue: 60,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/right-arm-new.png',
      },
    ],
  },
  {
    exampleImageSrc:
      storagePrefixFixedV2 + '/measure-girth-specification/example-image/thigh-circumference-specification.png',
    bodyPartName: '左/右大腿围',
    measuredItemValue: [
      {
        name: 'leftThighCircumference',
        label: '左大腿',
        unit: '厘米',
        imageFiledName: 'leftThighImage',
        value: 0,
        defaultValue: 50,
        minValue: 30,
        maxValue: 100,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/left-thigh.png',
      },
      {
        name: 'rightThighCircumference',
        label: '右大腿',
        unit: '厘米',
        imageFiledName: 'rightThighImage',
        value: 0,
        defaultValue: 50,
        minValue: 30,
        maxValue: 100,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/right-thigh-new.png',
      },
    ],
  },
  {
    exampleImageSrc: storagePrefixFixedV2 + '/measure-girth-specification/example-image/calf-girth-specification.png',
    bodyPartName: '左/右小腿围',
    measuredItemValue: [
      {
        name: 'leftCalfCircumference',
        label: '左小腿',
        unit: '厘米',
        imageFiledName: 'leftCalfImage',
        value: 0,
        defaultValue: 35,
        minValue: 15,
        maxValue: 100,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/left-calf.png',
      },
      {
        name: 'rightCalfCircumference',
        label: '右小腿',
        unit: '厘米',
        imageFiledName: 'rightCalfImage',
        value: 0,
        defaultValue: 35,
        minValue: 15,
        maxValue: 100,
        bodyPartImg: storagePrefixFixedV2 + '/measure-girth-specification/slider-rule/right-calf-new.png',
      },
    ],
  },
];

export const MeasurementValueByBodyPart: Record<Service.BodyPart, MeasurementValue[]> = {
  Shoulder: MEASURED_ITEM_VALUE_BY_BACK,
  Abdomen: MEASURED_ITEM_VALUE_BY_WAIST,
  Waist: MEASURED_ITEM_VALUE_BY_WAIST,
  Back: MEASURED_ITEM_VALUE_BY_BACK,
  All: MEASURED_ITEM_VALUE_ALL,
  LowerBody: MEASURED_ITEM_VALUE_ALL,
};
